import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import cytoscape, { Core } from 'cytoscape';
import familyTreeLogo from '../assets/family-tree-logo.png';
import { publicTreeBrandingUrl } from '../config/publicBranding';
import { brandClasses } from '../theme';
import {
  PublicCommentViewDto,
  PublicTreePersonDto,
  PublicTreeViewDto,
  buildApiBaseUrl,
  publicTreesAPI,
} from '../services/api';
import { SHARED_NODE_STYLE } from '../config/canvasStyles';

const BFF_BASE_URL = buildApiBaseUrl(process.env.REACT_APP_BFF_URL).replace(/\/api$/, '');

const resolvePhotoUrl = (photoUrl?: string | null): string | null => {
  if (!photoUrl) return null;
  // Convert /uploads/photos/{filename} -> {BFF_BASE_URL}/api/photos/{filename}
  const match = photoUrl.match(/\/uploads\/photos\/(.+)$/);
  if (match) {
    return `${BFF_BASE_URL}/api/photos/${match[1]}`;
  }
  return photoUrl;
};

const loadPhotoAsDataUrl = async (photoUrl: string): Promise<string | null> => {
  try {
    const url = resolvePhotoUrl(photoUrl);
    if (!url) return null;
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

type CommentTargetType = 'Tree' | 'Person';

const panelCardClassName = `rounded-2xl border bg-[#fffaf0]/95 p-5 shadow-[0_18px_34px_rgba(95,83,58,0.12)] ${brandClasses.softBorder}`;

const formatDate = (value?: string | null) => {
  if (!value) {
    return 'Unknown';
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown' : date.toLocaleDateString();
};

const commentTargetLabel = (selectedPerson: PublicTreePersonDto | null) =>
  selectedPerson ? `Commenting on ${selectedPerson.displayName}` : 'Commenting on this tree';

export const PublicTreePage: React.FC = () => {
  const { slug, token } = useParams<{ slug?: string; token: string }>();
  const cyRef = useRef<Core | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [tree, setTree] = useState<PublicTreeViewDto | null>(null);
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authorName, setAuthorName] = useState('');
  const [authorEmail, setAuthorEmail] = useState('');
  const [authorPhone, setAuthorPhone] = useState('');
  const [content, setContent] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentMessage, setCommentMessage] = useState<string | null>(null);

  const selectedPerson = useMemo(
    () => tree?.people.find((person) => person.id === selectedPersonId) ?? null,
    [selectedPersonId, tree?.people]
  );

  useEffect(() => {
    const loadTree = async () => {
      if (!token) {
        setError('Public tree token is missing.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = slug
          ? await publicTreesAPI.getPublicTreeBySlugAndToken(slug, token)
          : await publicTreesAPI.getPublicTreeByToken(token);
        setTree(response.data);
      } catch (loadError) {
        setError('Public tree could not be loaded.');
      } finally {
        setLoading(false);
      }
    };

    void loadTree();
  }, [slug, token]);

  useEffect(() => {
    if (!tree || !containerRef.current) {
      return;
    }

    cyRef.current?.destroy();

    const hasSavedPositions = tree.people.some((person) => person.x != null && person.y != null);
    const cy = cytoscape({
      container: containerRef.current,
      elements: [
        ...tree.people.map((person) => ({
          data: {
            id: person.id,
            label: person.displayName,
            isMasked: person.isMasked,
            photoUrl: 'none',
          },
          position: person.x != null && person.y != null ? { x: person.x, y: person.y } : undefined,
        })),
        ...tree.relationships.map((relationship) => ({
          data: {
            id: relationship.id,
            source: relationship.sourcePersonId,
            target: relationship.targetPersonId,
            label: relationship.relationshipType,
          },
        })),
      ],
      style: [
        {
          selector: 'node',
          style: {
            ...SHARED_NODE_STYLE,
            'background-color': '#e5e7eb',
            'color': '#3f3525',
          },
        },
        {
          selector: 'node[isMasked]',
          style: {
            'background-color': '#ece7d8',
            'border-style': 'dashed',
            'border-color': '#b39a6a',
          },
        },
        {
          selector: 'edge',
          style: {
            width: 2,
            'line-color': '#9c8b68',
            'target-arrow-color': '#9c8b68',
            'target-arrow-shape': 'triangle',
            'curve-style': 'bezier',
          },
        },
        {
          selector: ':selected',
          style: {
            'border-color': '#2e7d32',
            'border-width': 4,
          },
        },
      ],
      layout: hasSavedPositions ? { name: 'preset', fit: true, padding: 40 } : { name: 'breadthfirst', fit: true, padding: 60, directed: true },
      minZoom: 0.15,
      maxZoom: 4,
    });

    cy.on('tap', 'node', (event) => {
      setSelectedPersonId(event.target.id());
    });

    cy.on('tap', (event) => {
      if (event.target === cy) {
        setSelectedPersonId(null);
      }
    });

    cyRef.current = cy;

    // Load photos for non-masked people and set as node background
    tree.people.forEach((person) => {
      if (!person.isMasked && person.photoUrl) {
        void loadPhotoAsDataUrl(person.photoUrl).then((dataUrl) => {
          if (dataUrl) {
            const node = cy.getElementById(person.id);
            if (node && !node.empty()) {
              node.data('photoUrl', dataUrl);
            }
          }
        });
      }
    });

    return () => {
      cy.destroy();
      cyRef.current = null;
    };
  }, [tree]);

  const submitComment = async () => {
    if (!token || !tree) {
      return;
    }

    setSubmittingComment(true);
    setCommentMessage(null);

    try {
      const targetType: CommentTargetType = selectedPerson && !selectedPerson.isMasked ? 'Person' : 'Tree';
      const payload = {
        targetType,
        targetId: targetType === 'Person' ? selectedPerson?.id : null,
        authorName,
        authorEmail,
        authorPhone: authorPhone || null,
        content,
      };

      if (slug) {
        await publicTreesAPI.createCommentBySlugAndToken(slug, token, payload);
      } else {
        await publicTreesAPI.createCommentByToken(token, payload);
      }

      setCommentMessage('Comment submitted for moderation.');
      setContent('');
    } catch (submitError: any) {
      setCommentMessage(submitError?.response?.data?.error ?? 'Comment could not be submitted.');
    } finally {
      setSubmittingComment(false);
    }
  };

  const renderComments = (comments: PublicCommentViewDto[], depth = 0): React.ReactNode =>
    comments.map((comment) => (
      <div key={comment.id} className={`rounded-xl border bg-white/80 p-3 ${brandClasses.subtleBorder} ${depth > 0 ? 'ml-4 mt-3' : 'mt-3'}`}>
        <div className="flex items-center justify-between gap-3">
          <p className={`text-sm font-semibold ${brandClasses.primaryText}`}>{comment.authorName}</p>
          <p className={`text-xs ${brandClasses.subtleText}`}>{formatDate(comment.createdAt)}</p>
        </div>
        <p className={`mt-2 text-sm leading-6 ${brandClasses.bodyText}`}>{comment.content}</p>
        {comment.replies.length > 0 ? renderComments(comment.replies, depth + 1) : null}
      </div>
    ));

  const isCommentTargetAllowed = !selectedPerson || !selectedPerson.isMasked;

  if (loading) {
    return <div className={`min-h-screen flex items-center justify-center ${brandClasses.appShell}`}><p className={brandClasses.bodyText}>Loading public tree...</p></div>;
  }

  if (error || !tree) {
    return (
      <div className={`min-h-screen flex items-center justify-center px-6 ${brandClasses.appShell}`}>
        <div className={panelCardClassName}>
          <h1 className={`text-2xl font-bold ${brandClasses.titleText}`}>Public tree unavailable</h1>
          <p className={`mt-3 text-sm ${brandClasses.bodyText}`}>{error ?? 'This public tree could not be loaded.'}</p>
          <Link to="/login" className={`mt-5 inline-flex rounded-lg px-4 py-2 text-sm font-medium ${brandClasses.actionButton}`}>Go to login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${brandClasses.appShell}`}>
      <div className="mx-auto flex min-h-screen max-w-[1600px] flex-col px-4 py-4 lg:px-6">
        <div className={`${panelCardClassName} mb-4 flex flex-wrap items-start justify-between gap-4`}>
          <div>
            <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${brandClasses.subtleText}`}>Public Tree</p>
            <h1 className={`mt-2 text-3xl font-bold ${brandClasses.titleText}`}>{tree.treeName}</h1>
            {tree.treeDescription ? <p className={`mt-3 max-w-3xl text-sm leading-6 ${brandClasses.bodyText}`}>{tree.treeDescription}</p> : null}
          </div>
          <div className={`rounded-xl border px-4 py-3 text-sm ${brandClasses.subtleBorder} ${brandClasses.mutedSurface}`}>
            <p className={brandClasses.primaryText}>Published version: <strong>{tree.versionNumber}</strong></p>
            <p className={`mt-1 ${brandClasses.subtleText}`}>Published {formatDate(tree.publishedAt)}</p>
          </div>
        </div>

        <div className="grid min-h-[calc(100vh-180px)] gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className={`${panelCardClassName} relative overflow-hidden`}>
            <div ref={containerRef} className="h-[72vh] w-full rounded-xl bg-[#f8f2e6]" />

            <a
              href={publicTreeBrandingUrl || undefined}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit FamilyTree website"
              className="absolute bottom-4 left-4 z-20 rounded-xl bg-white/65 p-2 shadow-lg backdrop-blur transition hover:bg-white/85 focus:outline-none focus:ring-2 focus:ring-[#8a6d3b]"
              onClick={(event) => {
                if (!publicTreeBrandingUrl) {
                  event.preventDefault();
                }
              }}
            >
              <img src={familyTreeLogo} alt="FamilyTree" className="h-10 w-auto opacity-80" />
            </a>
          </div>

          <div className="space-y-4">
            <div className={panelCardClassName}>
              <h2 className={`text-xl font-semibold ${brandClasses.primaryText}`}>Person Details</h2>
              {selectedPerson ? (
                <div className="mt-4 space-y-3">
                  <p className={`text-lg font-semibold ${brandClasses.titleText}`}>{selectedPerson.displayName}</p>
                  {selectedPerson.photoUrl ? <img src={resolvePhotoUrl(selectedPerson.photoUrl) ?? undefined} alt={selectedPerson.displayName} className="h-40 w-full rounded-xl object-cover" /> : null}
                  <p className={`text-sm ${brandClasses.bodyText}`}>Born: {formatDate(selectedPerson.dateOfBirth)}</p>
                  {selectedPerson.dateOfDeath ? <p className={`text-sm ${brandClasses.bodyText}`}>Died: {formatDate(selectedPerson.dateOfDeath)}</p> : null}
                  {selectedPerson.shortDescription ? <p className={`text-sm ${brandClasses.bodyText}`}>{selectedPerson.shortDescription}</p> : null}
                  {selectedPerson.longDescription ? <p className={`text-sm leading-6 ${brandClasses.bodyText}`}>{selectedPerson.longDescription}</p> : null}
                </div>
              ) : (
                <p className={`mt-3 text-sm ${brandClasses.bodyText}`}>Select a person node to view public details.</p>
              )}
            </div>

            <div className={panelCardClassName}>
              <h2 className={`text-xl font-semibold ${brandClasses.primaryText}`}>Comments</h2>
              {tree.comments.length > 0 ? (
                <div className="mt-3 max-h-64 overflow-y-auto pr-1">
                  {renderComments(tree.comments)}
                </div>
              ) : (
                <p className={`mt-3 text-sm ${brandClasses.bodyText}`}>No approved comments yet.</p>
              )}
            </div>

            {tree.allowComments ? (
              <div className={panelCardClassName}>
                <h2 className={`text-xl font-semibold ${brandClasses.primaryText}`}>Leave a Comment</h2>
                <p className={`mt-2 text-sm ${brandClasses.subtleText}`}>{commentTargetLabel(selectedPerson)}</p>
                {!isCommentTargetAllowed ? (
                  <p className={`mt-3 text-sm ${brandClasses.bodyText}`}>Comments are not allowed on private or masked people.</p>
                ) : (
                  <div className="mt-4 space-y-3">
                    <input value={authorName} onChange={(event) => setAuthorName(event.target.value)} placeholder="Your name" className={`w-full rounded-lg border px-3 py-2 ${brandClasses.subtleBorder} ${brandClasses.appShell}`} />
                    <input value={authorEmail} onChange={(event) => setAuthorEmail(event.target.value)} placeholder="Your email" className={`w-full rounded-lg border px-3 py-2 ${brandClasses.subtleBorder} ${brandClasses.appShell}`} />
                    <input value={authorPhone} onChange={(event) => setAuthorPhone(event.target.value)} placeholder="Phone (optional)" className={`w-full rounded-lg border px-3 py-2 ${brandClasses.subtleBorder} ${brandClasses.appShell}`} />
                    <textarea value={content} onChange={(event) => setContent(event.target.value)} placeholder="Share a memory or note..." rows={5} className={`w-full rounded-lg border px-3 py-2 ${brandClasses.subtleBorder} ${brandClasses.appShell}`} />
                    {commentMessage ? <p className={`text-sm ${brandClasses.bodyText}`}>{commentMessage}</p> : null}
                    <button type="button" disabled={submittingComment || !authorName || !authorEmail || !content} onClick={() => void submitComment()} className={`rounded-lg px-4 py-2 text-sm font-medium ${brandClasses.actionButton} disabled:cursor-not-allowed disabled:opacity-60`}>
                      {submittingComment ? 'Submitting...' : 'Submit comment'}
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
